import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { getAllTenantsOptions, type TenantRead } from "@harmonyso/tenants-sdk";
import { CELLS, PRIMARY_CELL, cellListRequestOptions } from "@/lib/cells/cells";
import { cellQueryPolicy } from "@/lib/cells/cell-session";
import { mergeCellTenants, type CellFailure } from "@/lib/cells/cell-tenants";
import { rememberTenantCells } from "@/lib/cells/tenant-cells";

const combineCellTenants = (results: UseQueryResult<TenantRead[]>[]) => {
  const merged = mergeCellTenants(
    CELLS.map((cell, index) => ({
      cell,
      tenants: results.at(index)?.data,
      // A failed background refresh keeps the list already loaded.
      error: results.at(index)?.data ? null : results.at(index)?.error,
      isLoading: results.at(index)?.isLoading,
    })),
  );
  rememberTenantCells(merged.tenants);
  return {
    ...merged,
    isLoading: merged.tenants.length === 0 && merged.loadingCells.length > 0,
    allFailed: merged.failures.length === CELLS.length,
  };
};

/**
 * Tenants from every configured cell, merged into one list. The list is
 * usable as soon as any cell returns tenants; until then, and while any cell
 * is still loading, `isLoading` stays true so an empty list isn't shown
 * early. A cell that fails is reported in `failures` and one still loading
 * in `loadingCells`.
 */
export function useAllCellTenants({
  enabled = true,
  staleTime,
  gcTime,
}: { enabled?: boolean; staleTime?: number; gcTime?: number } = {}) {
  return useQueries({
    queries: CELLS.map((cell) => ({
      ...getAllTenantsOptions(cellListRequestOptions(cell.id)),
      enabled,
      staleTime,
      gcTime,
      ...cellQueryPolicy(cell),
    })),
    combine: combineCellTenants,
  });
}

/**
 * The cell serving a tenant: the explicit `?cell=` value when present,
 * otherwise looked up in the merged tenant list. `cellId` is undefined while
 * the lookup is still loading. `failures` lists the cells that couldn't be
 * searched, so a tenant missing from every list can still be in one of them.
 */
export function useTenantCell(
  tenantId: string,
  cellFromSearch: string | undefined,
): { cellId: string | undefined; failures: CellFailure[] } {
  const needsLookup = !cellFromSearch && CELLS.length > 1;
  const { tenants, failures, loadingCells } = useAllCellTenants({
    enabled: needsLookup,
  });

  if (!needsLookup) {
    return { cellId: cellFromSearch ?? PRIMARY_CELL.id, failures: [] };
  }
  const match = tenants.find((tenant) => tenant.id === tenantId);
  if (match?.deployment_cell) {
    return { cellId: match.deployment_cell, failures: [] };
  }
  return {
    cellId: loadingCells.length > 0 ? undefined : PRIMARY_CELL.id,
    failures,
  };
}
